import apiClient from "../../core/apiClient";
import { UserTaskType } from "../../interfaces/DTO/userTask/UserTask";
import { RemovalRequest, RemovalResult } from "../../interfaces/DTO/removal";
import {
  CreateWorkflowTaskRequest,
  FormsPreparationItem,
  ManualStepUpdateRequest,
  UserTaskDetails,
  UserTaskWithWorkflow,
  VerifyTasksRequest,
  VerifyTasksSummary,
  WorkflowDefinition,
} from "../../interfaces/DTO/userTask/UserTaskWorkflow";

const API_URL = "/user-tasks";

class UserTaskWorkflowService {
  /** deleted / cancelled / left as they are - by what already happened with each (backend TaskRemovalService) */
  async removeTasks(taskIds: number[], request: RemovalRequest): Promise<RemovalResult> {
    const response = await apiClient.post<RemovalResult>(`${API_URL}/remove`, { ...request, task_ids: taskIds });
    return response.data;
  }

  async fetchAllWithWorkflow(skip = 0, limit = 1000): Promise<UserTaskWithWorkflow[]> {
    const response = await apiClient.get<UserTaskWithWorkflow[]>(`${API_URL}/workflow`, {
      params: { skip, limit },
    });
    return response.data;
  }

  async fetchTaskDetails(taskId: number): Promise<UserTaskDetails> {
    const response = await apiClient.get<UserTaskDetails>(`${API_URL}/${taskId}/workflow`);
    return response.data;
  }

  /** the clients' tasks whose forms are not prepared yet - with the data to fill them */
  async fetchFormsPreparation(contactIds: number[]): Promise<FormsPreparationItem[]> {
    const params = new URLSearchParams();
    contactIds.forEach((id) => params.append("contact_ids", id.toString()));
    const response = await apiClient.get<FormsPreparationItem[]>(`${API_URL}/forms-preparation?${params.toString()}`);
    return response.data;
  }

  /**
   * the agent attaches a step's document (an image or a PDF the client sent) - stored safely on the server
   * (re-encoded / rebuilt) and completing the step, as the client's upload on the portal
   */
  async uploadStepDocument(taskId: number, stepCode: string, file: File): Promise<{ completed: boolean; document_path: string }> {
    const formData = new FormData();
    formData.append("file", file);
    const response = await apiClient.post(`${API_URL}/${taskId}/steps/${stepCode}/document`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  }

  async completeStep(
    taskId: number,
    stepCode: string,
    resultData?: Record<string, unknown>
  ): Promise<UserTaskDetails> {
    const response = await apiClient.post<UserTaskDetails>(
      `${API_URL}/${taskId}/steps/${stepCode}/complete`,
      { result_data: resultData ?? null }
    );
    return response.data;
  }

  async manualUpdateStep(
    taskId: number,
    stepCode: string,
    body: ManualStepUpdateRequest
  ): Promise<UserTaskDetails> {
    const response = await apiClient.patch<UserTaskDetails>(
      `${API_URL}/${taskId}/steps/${stepCode}/status`,
      body
    );
    return response.data;
  }

  async createWithWorkflow(data: CreateWorkflowTaskRequest): Promise<UserTaskDetails> {
    const response = await apiClient.post<UserTaskDetails>(`${API_URL}/workflow`, data);
    return response.data;
  }

  async fetchWorkflowDefinition(taskType: UserTaskType, changeType?: string): Promise<WorkflowDefinition> {
    const response = await apiClient.get<WorkflowDefinition>(`${API_URL}/workflow-definition/${taskType}`, {
      params: changeType ? { change_type: changeType } : undefined,
    });
    return response.data;
  }

  async verifyAllTasks(request: VerifyTasksRequest): Promise<VerifyTasksSummary> {
    const response = await apiClient.post<VerifyTasksSummary>(`${API_URL}/verify`, request);
    return response.data;
  }

  async verifyContactTasks(contactId: number, request: VerifyTasksRequest): Promise<VerifyTasksSummary> {
    const response = await apiClient.post<VerifyTasksSummary>(`${API_URL}/verify/${contactId}`, request);
    return response.data;
  }
}

export const userTaskWorkflowService = new UserTaskWorkflowService();
